/**
 * Preferências de tela por pessoa, guardadas no servidor (/preferencias) — valem em qualquer computador.
 * Cópia local (localStorage) só para abrir rápido; o servidor manda.
 *
 * Filtros lembrados:
 *  - useSavedState: filtros em estado da tela (useState);
 *  - useRememberedSearch: filtros na URL (?resp=…&tipo=…).
 * Datas: um período que termina HOJE é guardado como relativo ("últimos 30 dias" continua sendo os últimos 30 dias
 * amanhã); um período fixo fica fixo, e a tela avisa se ele não inclui o dia de hoje (FilterNotice).
 */
import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { create } from "zustand";
import { api } from "./api";
import { todayIso } from "./format";
import { useSearch } from "./router";

const LS = "vg:prefs";
type Data = Record<string, unknown>;

interface PrefStore {
  owner: string | null;
  loaded: boolean;
  data: Data;
  hydrate: (owner: string, data: Data) => void;
  set: (key: string, value: unknown) => void;
}

const fromLocal = (): { owner: string | null; data: Data } => {
  try { const v = JSON.parse(localStorage.getItem(LS) ?? "null") as { owner: string; data: Data } | null; return v ?? { owner: null, data: {} }; } catch { return { owner: null, data: {} }; }
};
const toLocal = (owner: string | null, data: Data) => { try { localStorage.setItem(LS, JSON.stringify({ owner, data })); } catch { /* sem armazenamento */ } };

/**
 * Gravações ainda não confirmadas pelo servidor (sobrevivem a recarregar a página): se a pessoa muda um filtro e
 * recarrega logo em seguida, o valor local vale e é reenviado ao carregar — o servidor não "desfaz" a escolha.
 */
const PENDING = "vg:prefs:pendentes";
const readPending = (): Record<string, unknown> => { try { return JSON.parse(localStorage.getItem(PENDING) ?? "{}") as Record<string, unknown>; } catch { return {}; } };
const writePending = (p: Record<string, unknown>) => { try { localStorage.setItem(PENDING, JSON.stringify(p)); } catch { /* sem armazenamento */ } };

const timers = new Map<string, ReturnType<typeof setTimeout>>();
function send(key: string, value: unknown): void {
  const enc = encodeURIComponent(key);
  void (value === null || value === undefined ? api.del(`/preferencias/${enc}`) : api.put(`/preferencias/${enc}`, { value }))
    .then(() => { const p = readPending(); if (JSON.stringify(p[key]) === JSON.stringify(value ?? null)) { delete p[key]; writePending(p); } })
    .catch(() => undefined);
}
function persist(key: string, value: unknown): void {
  writePending({ ...readPending(), [key]: value ?? null });
  const t = timers.get(key);
  if (t) clearTimeout(t);
  timers.set(key, setTimeout(() => { timers.delete(key); send(key, value); }, 700));
}

export const usePrefs = create<PrefStore>((set, get) => ({
  ...fromLocal(),
  loaded: false,
  hydrate: (owner, data) => {
    // Pendentes (mudanças locais ainda não confirmadas) prevalecem sobre o servidor e são reenviadas.
    const pend = get().owner === owner ? readPending() : {};
    const merged = { ...data };
    for (const [k, v] of Object.entries(pend)) { if (v === null) delete merged[k]; else merged[k] = v; send(k, v); }
    if (get().owner !== owner) writePending({});
    set({ owner, data: merged, loaded: true });
    toLocal(owner, merged);
  },
  set: (key, value) => {
    const data = { ...get().data };
    if (value === null || value === undefined) delete data[key]; else data[key] = value;
    set({ data });
    toLocal(get().owner, data);
    persist(key, value);
  },
}));

/** Carrega as preferências da pessoa logada (chamado uma vez, no Shell). */
export function usePrefsLoader(userId: string | null | undefined): void {
  const hydrate = usePrefs((s) => s.hydrate);
  const { data } = useQuery({ queryKey: ["preferencias", userId], queryFn: () => api.get<Data>("/preferencias"), enabled: !!userId, staleTime: Infinity });
  useEffect(() => { if (userId && data) hydrate(userId, data); }, [userId, data, hydrate]);
}

// ───────────────────────── datas relativas ─────────────────────────

const DATE = /^\d{4}-\d{2}-\d{2}$/;
const dayDiff = (a: string, b: string) => Math.round((Date.parse(`${b}T12:00:00Z`) - Date.parse(`${a}T12:00:00Z`)) / 86_400_000);
const addDaysIso = (d: string, n: number) => { const x = new Date(`${d}T12:00:00Z`); x.setUTCDate(x.getUTCDate() + n); return x.toISOString().slice(0, 10); };

/** Ao guardar: se alguma data do conjunto é HOJE, todas viram relativas ("@-30" = 30 dias atrás). */
function encodeDates(o: Record<string, unknown>, dateKeys: string[]): Record<string, unknown> {
  const t = todayIso();
  const dates = dateKeys.map((k) => o[k]).filter((v): v is string => typeof v === "string" && DATE.test(v));
  if (!dates.includes(t)) return o;
  const out = { ...o };
  for (const k of dateKeys) { const v = o[k]; if (typeof v === "string" && DATE.test(v)) out[k] = `@${dayDiff(t, v)}`; }
  return out;
}
function decodeDates(o: Record<string, unknown>): Record<string, unknown> {
  const t = todayIso();
  const out = { ...o };
  for (const [k, v] of Object.entries(o)) if (typeof v === "string" && /^@-?\d+$/.test(v)) out[k] = addDaysIso(t, Number(v.slice(1)));
  return out;
}

/** Período salvo (fixo) que não inclui hoje → texto de aviso; senão null. */
export function staleRange(from: string | null | undefined, to: string | null | undefined): string | null {
  if (!from && !to) return null;
  const t = todayIso();
  if ((from && from > t) || (to && to < t)) {
    const f = (d: string | null | undefined) => (d ? d.split("-").reverse().join("/") : "…");
    return `O período escolhido (${f(from)} a ${f(to)}) não inclui o dia de hoje.`;
  }
  return null;
}

// ───────────────────────── filtros em estado ─────────────────────────

export interface SavedMeta<T> {
  /** Os filtros vieram da última visita (não foram escolhidos agora). */
  restored: boolean;
  /** Campos diferentes do padrão. */
  changed: Array<keyof T>;
}

/**
 * Estado de filtros lembrado por pessoa. `defaults` é o "padrão" da tela (o que "Limpar filtros" restaura).
 * `opts.dateKeys`: campos de data a guardar como relativos quando o período termina hoje.
 * `opts.skip`: campos que não devem ser lembrados (ex.: busca por texto).
 */
export function useSavedState<T extends Record<string, unknown>>(key: string, defaults: T, opts: { dateKeys?: Array<keyof T & string>; skip?: Array<keyof T & string> } = {}):
  [T, (patch: Partial<T>) => void, () => void, SavedMeta<T>] {
  const prefKey = `filtros:${key}`;
  const loaded = usePrefs((s) => s.loaded);
  const saved = usePrefs((s) => s.data[prefKey]) as Partial<T> | undefined;
  const setPref = usePrefs((s) => s.set);
  const touched = useRef(false);
  const stateRef = useRef<T | null>(null);
  const build = (sv: Partial<T> | undefined): T => ({ ...defaults, ...(sv ? (decodeDates(sv as Record<string, unknown>) as Partial<T>) : {}) });
  const [state, setState] = useState<T>(() => build(saved));
  const [restored, setRestored] = useState<boolean>(() => !!saved && Object.keys(saved).length > 0);
  // Preferências chegaram do servidor depois de abrir a tela: aplica, se a pessoa ainda não mexeu.
  useEffect(() => {
    if (loaded && !touched.current && saved) { setState(build(saved)); setRestored(Object.keys(saved).length > 0); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loaded]);
  const save = (next: T) => {
    const diff: Record<string, unknown> = {};
    for (const k of Object.keys(next)) {
      if (opts.skip?.includes(k as keyof T & string)) continue;
      if (JSON.stringify(next[k]) !== JSON.stringify(defaults[k])) diff[k] = next[k];
    }
    setPref(prefKey, Object.keys(diff).length ? encodeDates(diff, (opts.dateKeys ?? []) as string[]) : null);
  };
  stateRef.current = state;
  // Grava fora do setState (nunca atualizar outro store durante a renderização).
  const update = (patch: Partial<T>) => {
    touched.current = true; setRestored(false);
    const next = { ...(stateRef.current ?? state), ...patch } as T;
    stateRef.current = next;
    setState(next);
    save(next);
  };
  const reset = () => { touched.current = true; setRestored(false); setState(defaults); setPref(prefKey, null); };
  const changed = (Object.keys(state) as Array<keyof T>).filter((k) => !opts.skip?.includes(k as keyof T & string) && JSON.stringify(state[k]) !== JSON.stringify(defaults[k]));
  return [state, update, reset, { restored, changed }];
}

// ───────────────────────── filtros na URL ─────────────────────────

/**
 * Filtros que moram na URL (?resp=…): ao abrir a tela sem nenhum deles na URL, restaura os lembrados;
 * a cada mudança, guarda. Retorna os filtros ativos e "limpar".
 */
export function useRememberedSearch(key: string, keys: string[], opts: { dateKeys?: string[] } = {}): { active: string[]; restored: boolean; clear: () => void; values: Record<string, string> } {
  const prefKey = `filtros:${key}`;
  const [search, set] = useSearch();
  const loaded = usePrefs((s) => s.loaded);
  const saved = usePrefs((s) => s.data[prefKey]) as Record<string, string> | undefined;
  const setPref = usePrefs((s) => s.set);
  const done = useRef(false);
  const [restored, setRestored] = useState(false);
  const values: Record<string, string> = {};
  for (const k of keys) { const v = search.get(k); if (v) values[k] = v; }
  const sig = JSON.stringify(values);

  const lastSig = useRef<string | null>(null);
  // Restaura uma vez (quando as preferências estiverem disponíveis).
  useEffect(() => {
    if (done.current) return;
    if (Object.keys(values).length > 0) { done.current = true; lastSig.current = sig; return; }
    if (!saved && !loaded) return; // espera o servidor
    done.current = true;
    if (saved && Object.keys(saved).length) {
      const dec = decodeDates(saved) as Record<string, string>;
      const next: Record<string, string> = {};
      for (const k of keys) if (dec[k]) next[k] = dec[k]!;
      lastSig.current = JSON.stringify(next);
      set(Object.fromEntries(keys.map((k) => [k, next[k] ?? null])));
      setRestored(true);
    } else lastSig.current = sig;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loaded, saved]);

  // Guarda a cada mudança feita na tela.
  useEffect(() => {
    if (!done.current || lastSig.current === null || sig === lastSig.current) return;
    lastSig.current = sig;
    setPref(prefKey, Object.keys(values).length ? encodeDates(values, opts.dateKeys ?? []) : null);
    setRestored(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sig]);

  return {
    active: Object.keys(values), restored, values,
    clear: () => { lastSig.current = "{}"; set(Object.fromEntries(keys.map((k) => [k, null]))); setPref(prefKey, null); setRestored(false); },
  };
}
